import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Leaf, Home, MapPin, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const About: React.FC = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const benefits = [
    {
      icon: Leaf,
      titleKey: 'about.benefits.pureOils.title',
      descriptionKey: 'about.benefits.pureOils.description',
      accent: 'bg-herb-green',
    },
    {
      icon: Home,
      titleKey: 'about.benefits.apartmentReady.title',
      descriptionKey: 'about.benefits.apartmentReady.description',
      accent: 'bg-amber-herb',
    },
    {
      icon: MapPin,
      titleKey: 'about.benefits.miltonMade.title',
      descriptionKey: 'about.benefits.miltonMade.description',
      accent: 'bg-forest',
    },
    {
      icon: Clock,
      titleKey: 'about.benefits.ritual.title',
      descriptionKey: 'about.benefits.ritual.description',
      accent: 'bg-herb-sage',
    },
  ];

  return (
    <section id="about" className="py-24 bg-white/60">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 max-w-3xl mx-auto"
        >
          <span className="inline-block text-herb-green text-sm font-semibold tracking-widest uppercase mb-4">
            What we do
          </span>
          <h2 className="text-4xl md:text-5xl font-elegant font-bold text-forest mb-5">
            {t('about.title')}
          </h2>
          <p className="text-lg text-warm-gray leading-relaxed font-italic italic mb-2">
            {t('about.subtitle')}
          </p>
          <p className="text-base text-charcoal/70 leading-relaxed mt-4">
            {t('about.description')}
          </p>
        </motion.div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: index * 0.15 }}
              whileHover={{ y: -6 }}
              className="bg-parchment/60 rounded-2xl p-7 border border-linen hover:border-herb-light/50 hover:shadow-md transition-all duration-300"
            >
              <div className={`w-12 h-12 ${benefit.accent} rounded-xl flex items-center justify-center mb-5`}>
                <benefit.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-forest mb-2">
                {t(benefit.titleKey)}
              </h3>
              <p className="text-sm text-warm-gray leading-relaxed">
                {t(benefit.descriptionKey)}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Philosophy quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="max-w-3xl mx-auto text-center"
        >
          <div className="bg-mist border border-herb-light/30 rounded-2xl p-10">
            <div className="w-8 h-1 bg-herb-green rounded mx-auto mb-6" />
            <p className="text-xl md:text-2xl font-italic italic text-forest leading-relaxed mb-4">
              {t('about.philosophy.quote')}
            </p>
            <p className="text-sm text-warm-gray font-semibold tracking-wide uppercase">
              — NestBloom
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
